// Copyright 2016 Sorama B.V.
import { CaptureDevice, FrontEnd, FrontEndConnector, SensorCalibration } from "datatypes/SoramaLocalApi";
import { MeasurementDevice } from "services/Hardware/Datatypes/MeasurementDevice";

export class RegistrableDevice<TDevice> {
    originalDevice: TDevice;
    moduleId: string;
    physicalId: string;
}

export class RegistrableMeasurementDevice extends RegistrableDevice<MeasurementDevice> {
    public frontEnd: RegistrableFrontEnd;
    public captureDevices: RegistrableDevice<CaptureDevice>[];

    /**
     * Create a Cachable Device.
     * @param measurementDevice
     */
    public static createRegistrableCachedDevice(measurementDevice: MeasurementDevice): RegistrableMeasurementDevice {
        // make sure that we ALWAYS have calibration data for cached devices
        for (const connector of measurementDevice.frontEnd.frontEndConnectors) {
            if (!connector.sensorCalibrations) throw new Error("Requested device does not contain Calibration Data!");

            for (const sensor of connector.sensorCalibrations) {
                if (!sensor.calibrationData) throw new Error("Requested device does not contain Calibration Data!");
            }
        }

        return this.getRegisterableDevice(measurementDevice);
    }

    private static getRegisterableDevice(
        measurementDevice: MeasurementDevice,
        moduleIdPrefix = ""
    ): RegistrableMeasurementDevice {
        const registrableMeasurementDevice = new RegistrableMeasurementDevice();

        const frontEnd = new RegistrableFrontEnd();

        frontEnd.originalDevice = measurementDevice.frontEnd;
        frontEnd.moduleId = moduleIdPrefix + "RCA" + measurementDevice.frontEnd.id;
        frontEnd.physicalId = moduleIdPrefix + measurementDevice.name;

        frontEnd.registrableFrontEndConnectors = measurementDevice.frontEnd.frontEndConnectors.map((feConnector) => {
            const connector = new RegistrableFrontEndConnector();
            connector.moduleId = moduleIdPrefix + feConnector.id;
            connector.physicalId = moduleIdPrefix + feConnector.connectionInfo;
            connector.originalDevice = feConnector;

            const numberOfMicsPerArray = feConnector.microphoneCount / feConnector.microphoneArrayIds.length;

            connector.registrableCalibratedMicrophoneArray = feConnector.microphoneArrayIds.map((arrayId) => {
                const mic = new RegistrableDevice<SensorCalibration>();
                const calibration: number[] = new Array(numberOfMicsPerArray).fill(14.0); // Default calibration value

                mic.originalDevice = {
                    sensorArrayId: arrayId,
                    calibrationData: calibration,
                };
                mic.moduleId = arrayId;
                mic.physicalId = arrayId;
                return mic;
            });

            return connector;
        });

        registrableMeasurementDevice.frontEnd = frontEnd;
        registrableMeasurementDevice.captureDevices = [];
        registrableMeasurementDevice.originalDevice = measurementDevice;

        for (let i = 0; i < measurementDevice.captureDevices.length; i++) {
            const device = measurementDevice.captureDevices[i];

            // Skip duplicate devices, this will only cause weird issues since we cannot distinguish 2 identical capture devices.
            if (
                registrableMeasurementDevice.captureDevices.some(
                    (x) =>
                        x.originalDevice.vendorId == device.vendorId && x.originalDevice.productId == device.productId
                )
            )
                continue;

            const captureDevice = new RegistrableDevice<CaptureDevice>();
            captureDevice.originalDevice = device;
            captureDevice.originalDevice.name = moduleIdPrefix + captureDevice.originalDevice.name;
            captureDevice.moduleId = moduleIdPrefix + measurementDevice.captureDevices[i].id + frontEnd.moduleId;
            captureDevice.physicalId = moduleIdPrefix + measurementDevice.captureDevices[i].name;
            registrableMeasurementDevice.captureDevices.push(captureDevice);
        }

        return registrableMeasurementDevice;
    }
}

export class RegistrableFrontEnd extends RegistrableDevice<FrontEnd> {
    registrableFrontEndConnectors: RegistrableFrontEndConnector[];
}

export class RegistrableFrontEndConnector extends RegistrableDevice<FrontEndConnector> {
    registrableCalibratedMicrophoneArray: RegistrableDevice<SensorCalibration>[];
}
